import { createFileRoute, Link } from "@tanstack/react-router";
import { Faq } from "@/components/layouts/Faq";
import { RegisterForm } from "./-components/RegisterForm";
import { OnboardingForm } from "./-components/OnboardingForm";
import { EmailVerificationForm } from "./-components/EmailVerificationForm";
import { Image } from "@/components/ui/image";
import { AvatarCircles } from "@/components/ui/avatar-circles";
import { useState } from "react";
import { z } from "zod/v4";
import { Wallet, Calendar, Phone, ShieldCheck, ArrowLeft, Star } from "lucide-react";
import { createSEOHead, SEO_DATA } from "@/lib/seo";

const avatarUrls = Array.from(
  { length: 6 },
  (_, index) => `/ipx/f_webp,q_75,w_96/images/avatar_${index + 1}.webp`
);

const inscriptionSearchSchema = z.object({
  role: z.enum(["chef", "customer"]).optional(),
});

export const Route = createFileRoute("/(public)/(auth)/inscription")({
  head: () => createSEOHead(SEO_DATA.SIGNUP),
  validateSearch: (search) => {
    const result = inscriptionSearchSchema.safeParse(search);
    return result.success ? result.data : {};
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { role: lockedRole } = Route.useSearch();
  const [currentStep, setCurrentStep] = useState<
    "register" | "onboarding" | "verification"
  >("register");
  const [customerEmail, setCustomerEmail] = useState<string>("");
  const [customerId, setCustomerId] = useState<number | null>(null);

  const handleChefRegistered = () => {
    setCurrentStep("onboarding");
  };

  const handleCustomerRegistered = (email: string, userId: number) => {
    setCustomerEmail(email);
    setCustomerId(userId);
    setCurrentStep("verification");
  };

  const isChef = lockedRole === "chef";

  return (
    <section>
      <div className="margin-top max-md:px-4 overflow-hidden relative">
        {/* Container avec slide effect */}
        <div
          className={`flex transition-transform duration-700 ease-in-out ${
            currentStep === "register"
              ? "translate-x-0"
              : currentStep === "onboarding"
              ? "-translate-x-full"
              : "-translate-x-[200%]"
          }`}
        >
          {/* Page d'inscription */}
          <div className="min-w-full flex justify-center gap-10">
            {isChef ? (
              <ChefRegisterLayout
                onChefRegistered={handleChefRegistered}
                onCustomerRegistered={handleCustomerRegistered}
              />
            ) : (
              <>
                <RegisterForm
                  onChefRegistered={handleChefRegistered}
                  onCustomerRegistered={handleCustomerRegistered}
                  lockedRole={lockedRole}
                />
                <div className="hidden lg:block bg-dark-blue rounded-lg p-10 max-w-[750px]">
                  <h2 className="text-white font-h2 text-center mb-6">
                    Rejoignez HomeToq, la plateforme qui révolutionne les chefs
                    à domicile
                  </h2>
                  <p className="text-white text-center font-p">
                    Chef ou client, inscrivez-vous et découvrez une nouvelle
                    expérience culinaire.
                  </p>
                  <Image
                    width={610}
                    height={500}
                    src="/images/dashboard.webp"
                    className="mx-auto"
                    layout="constrained"
                  />
                  <AvatarCircles
                    avatarUrls={avatarUrls}
                    numPeople={150}
                    className="mx-auto w-fit"
                  />
                  <h4 className="font-h4 mb-2 text-center text-white">
                    +150 chefs déjà inscrits
                  </h4>
                </div>
              </>
            )}
          </div>

          {/* Page d'onboarding chef */}
          <div className="min-w-full flex justify-center gap-10">
            <OnboardingForm />
          </div>

          {/* Page de vérification email client */}
          <div className="min-w-full flex justify-center gap-10">
            <EmailVerificationForm email={customerEmail} userId={customerId} />
          </div>
        </div>
      </div>
      <Faq />
    </section>
  );
}

const STEPS = [
  { label: "Inscription", sub: "Vos infos de base" },
  { label: "Profil chef", sub: "Vos plats & dispos" },
  { label: "Validation", sub: "Sous 48h" },
];

const VALUE_PROPS = [
  {
    icon: Wallet,
    title: "10% de commission",
    body: "Pas d'abonnement, pas de frais d'inscription.",
  },
  {
    icon: Calendar,
    title: "Vos dispos, vos règles",
    body: "Travaillez quand vous voulez, à votre rythme.",
  },
  {
    icon: Phone,
    title: "Rendez-vous téléphonique",
    body: "Notre équipe vous appelle pour faire connaissance.",
  },
  {
    icon: ShieldCheck,
    title: "Aucun engagement",
    body: "Vous restez libre à tout moment, sans frais.",
  },
];

function ChefRegisterLayout({
  onChefRegistered,
  onCustomerRegistered,
}: {
  onChefRegistered: () => void;
  onCustomerRegistered: (email: string, userId: number) => void;
}) {
  const heroAvatars = Array.from(
    { length: 4 },
    (_, i) => `/ipx/f_webp,q_75,w_96/images/avatar_${i + 1}.webp`,
  );

  return (
    <div className="w-full max-w-6xl px-2">
      {/* Retour vers la landing */}
      <Link
        to="/devenir-chef"
        className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-primary font-button-small transition-colors mb-6"
      >
        <ArrowLeft size={14} />
        Retour à la présentation
      </Link>

      {/* Stepper */}
      <div className="mb-10">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          {STEPS.map((step, idx) => {
            const isActive = idx === 0;
            const isLast = idx === STEPS.length - 1;
            return (
              <div key={step.label} className="flex items-center flex-1">
                <div className="flex flex-col items-center text-center min-w-0">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0 transition-colors ${
                      isActive
                        ? "bg-primary text-white"
                        : "bg-cream border-2 border-border text-muted-foreground"
                    }`}
                  >
                    {idx + 1}
                  </div>
                  <p
                    className={`font-button-small mt-2 ${
                      isActive ? "text-dark-blue" : "text-muted-foreground"
                    }`}
                  >
                    {step.label}
                  </p>
                  <p className="font-small text-muted-foreground mt-0.5 max-md:hidden">
                    {step.sub}
                  </p>
                </div>
                {!isLast && (
                  <div className="flex-1 h-px bg-border mx-2 -mt-8" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Form + panel droit */}
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
        {/* Form (gauche) */}
        <div className="bg-white rounded-2xl border border-border p-8 lg:p-10 shadow-sm">
          <RegisterForm
            onChefRegistered={onChefRegistered}
            onCustomerRegistered={onCustomerRegistered}
            lockedRole="chef"
          />
        </div>

        {/* Panel chef (droite) */}
        <aside className="hidden lg:flex flex-col gap-6 sticky top-8">
          {/* Photo + overlay value */}
          <div className="relative rounded-2xl overflow-hidden h-64">
            <Image
              src="/images/find_chef_image.webp"
              alt="Chef HomeToq"
              layout="fullWidth"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-blue/85 via-dark-blue/30 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 text-white">
              <p className="font-h3 leading-tight">
                Bienvenue dans la cuisine HomeToq
              </p>
              <p className="font-small mt-1.5 text-white/80">
                Encore quelques étapes avant votre première prestation.
              </p>
            </div>
          </div>

          {/* Value props */}
          <div className="grid grid-cols-2 gap-3">
            {VALUE_PROPS.map((vp) => {
              const Icon = vp.icon;
              return (
                <div
                  key={vp.title}
                  className="bg-white rounded-xl border border-border p-4 hover:border-primary/30 transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mb-2">
                    <Icon size={16} className="text-primary" />
                  </div>
                  <p className="font-button-small text-dark-blue leading-tight">
                    {vp.title}
                  </p>
                  <p className="font-small text-muted-foreground mt-1 leading-snug">
                    {vp.body}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Social proof */}
          <div className="bg-cream/50 rounded-xl border border-border px-4 py-3 flex items-center gap-3">
            <AvatarCircles
              avatarUrls={heroAvatars}
              numPeople={150}
              className="w-fit"
            />
            <div className="border-l border-border pl-3 flex-1">
              <div className="flex gap-0.5 mb-0.5">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <Star
                    key={idx}
                    size={11}
                    className="text-primary"
                    fill="currentColor"
                  />
                ))}
              </div>
              <p className="font-small text-muted-foreground">
                <strong className="text-dark-blue">+150 chefs</strong> nous ont
                rejoints
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
